import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaUser, FaSearch, FaTimes, FaPlus } from 'react-icons/fa';
import './DefendantSearch.css';

const DefendantSearch = ({ onDefendantSelect, initialValue = '' }) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedDefendant, setSelectedDefendant] = useState(null);
  const dropdownRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Debounce search
    if (searchTerm.length >= 2 && !selectedDefendant) {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }

      searchTimeout.current = setTimeout(() => {
        searchDefendants(searchTerm);
      }, 300);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [searchTerm, selectedDefendant]);

  const searchDefendants = async (query) => {
    try {
      setIsSearching(true);
      console.log('Searching for:', query);
      const response = await axios.get(`/defendants`, {
        params: {
          search: query,
          limit: 10
        }
      });

      console.log('Search response:', response.data);
      console.log('Found defendants:', response.data.data);

      if (response.data.success) {
        setSearchResults(response.data.data);
        setShowDropdown(true);
      }
    } catch (error) {
      console.error('Error searching defendants:', error);
      console.error('Error details:', error.response?.data);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectExisting = (defendant) => {
    setSelectedDefendant(defendant);
    setSearchTerm(`${defendant.first_name} ${defendant.middle_name || ''} ${defendant.last_name}`.trim());
    setShowDropdown(false);

    // Pass the existing defendant data to parent
    onDefendantSelect({
      isExisting: true,
      defendantId: defendant.id,
      data: {
        firstName: defendant.first_name || '',
        middleName: defendant.middle_name || '',
        lastName: defendant.last_name || '',
        dateOfBirth: defendant.date_of_birth ? defendant.date_of_birth.split('T')[0] : '',
        ssn: defendant.ssn || '',
        phonePrimary: defendant.phone_primary || '',
        phoneSecondary: defendant.phone_secondary || '',
        email: defendant.email || '',
        addressStreet: defendant.address_street || '',
        addressCity: defendant.address_city || '',
        addressState: defendant.address_state || 'MT',
        addressZip: defendant.address_zip || '',
        height: defendant.height || '',
        weight: defendant.weight || '',
        hairColor: defendant.hair_color || '',
        eyeColor: defendant.eye_color || '',
        gender: defendant.gender || '',
        employer: defendant.employer || '',
        occupation: defendant.occupation || ''
      }
    });
  };

  const handleCreateNew = () => {
    setSelectedDefendant(null);
    setShowDropdown(false);

    // Clear form and allow new entry
    onDefendantSelect({
      isExisting: false,
      defendantId: null,
      data: {
        firstName: '',
        middleName: '',
        lastName: '',
        dateOfBirth: '',
        ssn: '',
        phonePrimary: '',
        phoneSecondary: '',
        email: '',
        addressStreet: '',
        addressCity: '',
        addressState: 'MT',
        addressZip: '',
        height: '',
        weight: '',
        hairColor: '',
        eyeColor: '',
        gender: '',
        employer: '',
        occupation: ''
      }
    });
  };

  const handleClearSelection = () => {
    setSelectedDefendant(null);
    setSearchTerm('');
    setSearchResults([]);
    handleCreateNew();
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (selectedDefendant) {
      setSelectedDefendant(null);
      handleCreateNew();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="defendant-search-container" ref={dropdownRef}>
      <div className="search-input-wrapper">
        <FaSearch className="search-icon" />
        <input
          type="text"
          className={`defendant-search-input ${selectedDefendant ? 'has-selection' : ''}`}
          placeholder="Search existing defendants by name..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => searchTerm.length >= 2 && !selectedDefendant && setShowDropdown(true)}
        />
        {selectedDefendant && (
          <button
            type="button"
            className="clear-selection-btn"
            onClick={handleClearSelection}
            title="Clear and create new defendant"
          >
            <FaTimes />
          </button>
        )}
        {isSearching && <div className="search-spinner"></div>}
      </div>

      {selectedDefendant && (
        <div className="selected-defendant-badge">
          <FaUser />
          <span>Using existing defendant: <strong>{selectedDefendant.first_name} {selectedDefendant.last_name}</strong></span>
        </div>
      )}

      {showDropdown && !selectedDefendant && (
        <div className="defendant-search-dropdown">
          {searchResults.length > 0 ? (
            <>
              <div className="dropdown-header">
                Found {searchResults.length} existing defendant{searchResults.length !== 1 ? 's' : ''}
              </div>
              {searchResults.map((defendant) => (
                <div
                  key={defendant.id}
                  className="defendant-result-item"
                  onClick={() => handleSelectExisting(defendant)}
                >
                  <div className="result-main">
                    <div className="result-name">
                      <FaUser className="result-icon" />
                      {defendant.first_name} {defendant.middle_name || ''} {defendant.last_name}
                    </div>
                    <div className="result-details">
                      <span>DOB: {formatDate(defendant.date_of_birth)}</span>
                      {defendant.phone_primary && <span> • Phone: {defendant.phone_primary}</span>}
                      {defendant.address_city && defendant.address_state && (
                        <span> • {defendant.address_city}, {defendant.address_state}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div className="dropdown-divider"></div>
            </>
          ) : (
            <div className="dropdown-no-results">
              No existing defendants found matching "{searchTerm}"
            </div>
          )}
          <div
            className="defendant-result-item create-new"
            onClick={handleCreateNew}
          >
            <FaPlus className="result-icon" />
            <span>Create New Defendant</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DefendantSearch;
